import React, { useMemo } from "react";
import { Alert, Card, Col, Empty, List, Row, Skeleton, Space, Tag, Typography } from "antd";
import { AppstoreOutlined, CheckCircleOutlined, UserOutlined } from "@ant-design/icons";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { BASE_URL } from "../../../utils/constants";
import { Action } from "../../../utils/interfaces";
import { useCurrentUser } from "../../../hooks/useCurrentUser";

const { Title, Text } = Typography;

const HomePageView: React.FC = () => {
  const { data: currentUser, isLoading: userLoading } = useCurrentUser();
  const isAdmin = currentUser?.roleName === "Admin";

  // Admin bypasses the Action system entirely (see app.py's before_request)
  // and has no meaningful per-action list to show, so don't even fetch it.
  const actionsQuery = useQuery<Action[]>({
    queryKey: ["actions"],
    queryFn: async () => {
      const res = await axios.get<Action[]>(`${BASE_URL}actions`);
      return res.data;
    },
    enabled: !!currentUser && !isAdmin,
  });

  const assignedActionsByCategory = useMemo(() => {
    const groups: Record<string, Action[]> = {};
    if (!currentUser || !actionsQuery.data) return groups;
    const assigned = actionsQuery.data.filter((action: Action) => currentUser.allowedActions.includes(action.key));
    for (const action of assigned) {
      const categoryName = action.action_category?.name ?? "Uncategorized";
      (groups[categoryName] ||= []).push(action);
    }
    return groups;
  }, [currentUser, actionsQuery.data]);

  if (userLoading) {
    return (
      <div style={{ padding: 24 }}>
        <Skeleton active />
      </div>
    );
  }

  if (!currentUser) {
    return (
      <div style={{ padding: 24 }}>
        <Alert type="warning" showIcon message="You are not signed in." />
      </div>
    );
  }

  const categoryEntries = Object.entries(assignedActionsByCategory);

  return (
    <div style={{ padding: 24 }}>
      <Title level={2}>Welcome, {currentUser.name}</Title>

      <Card style={{ marginBottom: 24, maxWidth: 480 }}>
        <Space direction="vertical" size={4}>
          <Space>
            <UserOutlined />
            <Text strong>{currentUser.name}</Text>
          </Space>
          <Space>
            <Text type="secondary">Role:</Text>
            <Tag color={isAdmin ? "gold" : "blue"}>{currentUser.roleName}</Tag>
          </Space>
        </Space>
      </Card>

      {isAdmin ? (
        <Alert
          style={{ maxWidth: 640 }}
          type="info"
          showIcon
          message="As an Administrator, you have full access to every page and action."
        />
      ) : (
        <>
          <Title level={4}>Your Actions</Title>
          {actionsQuery.isLoading ? (
            <Skeleton active />
          ) : categoryEntries.length === 0 ? (
            <Empty description="No actions assigned yet. Contact an admin to request access." />
          ) : (
            <Row gutter={[16, 16]}>
              {categoryEntries.map(([categoryName, actions]) => (
                <Col xs={24} sm={12} lg={8} key={categoryName}>
                  <Card
                    size="small"
                    title={
                      <Space>
                        <AppstoreOutlined />
                        <span>{categoryName}</span>
                      </Space>
                    }
                    extra={<Tag>{actions.length}</Tag>}
                    styles={{ body: { paddingTop: 4 } }}
                  >
                    <List
                      size="small"
                      dataSource={actions}
                      renderItem={(action) => (
                        <List.Item style={{ padding: "6px 0", border: "none" }}>
                          <Space>
                            <CheckCircleOutlined style={{ color: "#52c41a" }} />
                            <Text>{action.name}</Text>
                          </Space>
                        </List.Item>
                      )}
                    />
                  </Card>
                </Col>
              ))}
            </Row>
          )}
        </>
      )}
    </div>
  );
};

export default HomePageView;
